// src/core/poseSolver.js
//
// PointAI's own head-pose solver (roadmap item 1).
//
// WHY: MediaPipe's facial transformation matrix is solved against a fixed 63°
// virtual camera. On a landscape webcam (~41° vertical) that places the head
// ~1.6× too close and over-rotates yaw; we corrected depth in scene.js, but the
// rotation error and the hard-coded head size remain. This solver refits the
// canonical face model to the 2D landmarks under the CALIBRATED camera (the
// real vertical FOV + aspect), so pose, perspective and — optionally — the
// user's true head scale are all consistent with what the camera actually saw.
//
// MODEL:  world_i = s · R · p_i + t          (p_i = canonical vertex, cm)
//         u_i = (f / aspect) · X_i / (-Z_i)  (NDC x, -1..1)
//         v_i =  f           · Y_i / (-Z_i)  (NDC y)
//         f   = 1 / tan(vfov / 2)
//         residual_i = w_i · (proj(world_i) - obs_i)
//
// Solved by damped Gauss-Newton (Levenberg-Marquardt) on 6 pose parameters
// (rotation as a left-multiplied small-angle update, translation) with the
// scale s held fixed (1.0 = canonical, or PD-derived — see setScale). Warm-
// started from the previous frame; initialised from MediaPipe's matrix on the
// first frame or after a divergence. Analytic Jacobian, ~4 iterations,
// < 1 ms for ~470 points.
//
// Weights: rigid regions (nose, face oval, brows, cheeks, eye corners) weigh 1;
// eyelids 0.2 (blinks); lips 0 (speech); everything else 0.4.
//
// Output: a column-major 4×4 (same convention as MediaPipe's matrix: canonical
// face → camera space, camera at origin looking down -Z, cm), so it drops into
// MatrixSmoother / Glasses / occluders unchanged.

import { Matrix4, Matrix3, Vector3, Quaternion } from 'three';
import { CANONICAL_FACE_VERTICES } from '../web/canonicalFace.js';

const N = 468;

// ── landmark weights ─────────────────────────────────────────────────────────
const LIPS = [61,146,91,181,84,17,314,405,321,375,291,308,324,318,402,317,14,87,178,88,95,78,191,80,81,82,13,312,311,310,415,409,270,269,267,0,37,39,40,185];
const EYELIDS = [7,163,144,145,153,154,155,246,161,160,159,158,157,173, 249,390,373,374,380,381,382,466,388,387,386,385,384,398];
const RIGID = [
  // nose
  1,2,4,5,6,19,44,45,48,49,51,59,60,64,75,79,94,97,98,99,100,101,102,114,115,120,121,122,125,126,128,129,131,134,141,166,168,174,188,193,195,196,197,198,203,209,217,218,219,220,236,237,238,239,240,241,242,244,245,248,250,274,275,278,279,281,289,290,294,305,309,324,326,327,328,329,330,331,332,344,345,350,351,352,355,356,358,360,363,370,392,393,399,412,413,417,419,420,423,429,437,438,439,440,441,455,456,457,458,459,460,461,462,464,465,
  // eye corners
  33,133,362,263,
  // face oval
  10,338,297,332,284,251,389,356,454,323,361,288,397,365,379,378,400,377,152,148,176,149,150,136,172,58,132,93,234,127,162,21,54,103,67,109,
  // brows
  70,63,105,66,107,55,65,52,53,46,300,293,334,296,336,285,295,282,283,276,
  // cheeks
  50,118,117,111,116,123,147,187,207,206,280,347,346,340,376,411,427,426
];
const WEIGHTS = new Float32Array(N).fill(0.4);
for (const i of RIGID) WEIGHTS[i] = 1.0;
for (const i of EYELIDS) WEIGHTS[i] = 0.2;
for (const i of LIPS) WEIGHTS[i] = 0.0;

// Canonical inter-pupillary distance (midpoints of 33/133 and 362/263), cm.
export const CANONICAL_PD_CM = 6.302;

export class PoseSolver {
  constructor({ iterations = 4, lambda = 1e-3, rotationFrom = 'mediapipe' } = {}) {
    this.iterations = iterations;
    this.lambda0 = lambda;
    // 'mediapipe' (hybrid, default): rotation is taken from MediaPipe's matrix
    // every frame (its 3D-to-3D fit is insensitive to the camera FOV we assume),
    // and only translation/depth is solved under the calibrated camera — which
    // is what fixes far-lens perspective. 'solve': full 6-DoF fit. The 6-DoF fit
    // is exact when the assumed FOV is right, but on a wide camera with the
    // face low in the frame a too-narrow assumed FOV is explained as HEAD PITCH
    // (frames tipped forward, lenses flattened), so it is opt-in.
    this.rotationFrom = rotationFrom;
    this.f = 1 / Math.tan((63 * Math.PI / 180) / 2);
    this.aspect = 16 / 9;
    this.scale = 1.0;

    this.R = new Matrix3().identity();
    this.t = new Vector3(0, 0, -45);
    this._hasPose = false;
    this.rmsPx = 0;          // last residual RMS in NDC (×1000 for readability)
    this.iterationsUsed = 0;

    // Scratch
    this._out = new Matrix4();
    this._q = new Quaternion();
    this._dR = new Matrix3();
    this._JtJ = new Float64Array(36);
    this._Jtr = new Float64Array(6);
    this._x = new Float64Array(6);
    this._world = new Float32Array(N * 3);
  }

  /** Camera intrinsics: vertical FOV (deg) and aspect (w/h) of the capture. */
  setCamera(vfovDeg, aspect) {
    this.f = 1 / Math.tan((vfovDeg * Math.PI / 180) / 2);
    this.aspect = aspect;
  }

  /** Canonical→user scale. 1 = MediaPipe's fixed-size assumption. */
  setScale(s) { this.scale = Math.max(0.7, Math.min(1.4, s)); }
  getScale() { return this.scale; }

  /** Seed the pose from an existing column-major matrix (e.g. MediaPipe's M). */
  initFrom(matrix16) {
    const m = matrix16;
    this.R.set(m[0], m[4], m[8], m[1], m[5], m[9], m[2], m[6], m[10]);
    // strip any scale MediaPipe baked into the rotation columns
    const e = this.R.elements;
    const sx = Math.hypot(e[0], e[1], e[2]) || 1;
    for (let i = 0; i < 9; i++) e[i] /= sx;
    this.t.set(m[12], m[13], m[14]);
    this._hasPose = true;
  }

  reset() { this._hasPose = false; }

  /**
   * Refine the pose against the current landmarks.
   * @param {Array<{x,y}>} landmarks  normalised image coords (0..1, y down)
   * @param {Float32Array|Array} [initMatrix]  MediaPipe matrix for cold start
   * @returns {Float32Array} column-major 4×4 (cm)
   */
  solve(landmarks, initMatrix) {
    if (!this._hasPose) {
      if (initMatrix) this.initFrom(initMatrix);
      else { this.R.identity(); this.t.set(0, 0, -45); this._hasPose = true; }
    } else if (this.rotationFrom === 'mediapipe' && initMatrix) {
      // Hybrid: take MediaPipe's rotation each frame, keep our translation warm.
      const tx = this.t.x, ty = this.t.y, tz = this.t.z;
      this.initFrom(initMatrix);
      this.t.set(tx, ty, tz);
    }
    const solveRot = this.rotationFrom !== 'mediapipe';
    const f = this.f, fx = f / this.aspect, s = this.scale;
    const cv = CANONICAL_FACE_VERTICES;
    const R = this.R.elements;   // column-major 3×3
    const t = this.t;
    const JtJ = this._JtJ, Jtr = this._Jtr, x = this._x;
    let lambda = this.lambda0;
    let lastCost = Infinity;

    for (let it = 0; it < this.iterations; it++) {
      JtJ.fill(0); Jtr.fill(0);
      let cost = 0, wsum = 0;

      for (let i = 0; i < N; i++) {
        const w = WEIGHTS[i];
        if (w === 0) continue;
        const lm = landmarks[i];
        if (!lm) continue;
        const px = cv[i*3] * s, py = cv[i*3+1] * s, pz = cv[i*3+2] * s;
        // world = R p + t
        const X = R[0]*px + R[3]*py + R[6]*pz + t.x;
        const Y = R[1]*px + R[4]*py + R[7]*pz + t.y;
        const Z = R[2]*px + R[5]*py + R[8]*pz + t.z;
        if (Z > -1) continue;                    // behind / at camera
        const invZ = -1 / Z;                     // 1 / depth  (depth = -Z > 0)
        const u = fx * X * invZ, v = f * Y * invZ;
        const ru = u - (lm.x * 2 - 1);
        const rv = v - (-(lm.y * 2 - 1));
        cost += w * (ru*ru + rv*rv); wsum += w;

        // d(u,v)/d(world):  u = fx X / D, D = -Z  →  du/dX = fx/D, du/dZ = fx X / D²
        const duX = fx * invZ, duZ = fx * X * invZ * invZ;
        const dvY = f  * invZ, dvZ = f  * Y * invZ * invZ;
        // d(world)/d(params): params = [ωx, ωy, ωz, tx, ty, tz]; a small rotation
        // δR = I + [ω]× applied on the LEFT of R: d(world)/dω = -[q]× where q = R p
        const qx = X - t.x, qy = Y - t.y, qz = Z - t.z;
        // Jacobian rows (2×6):
        //   J_u = [ duX·(0) + duZ·(0)... ] computed via dworld/dω = ω × q:
        //   dworld/dωx = ( 0, -qz·(-1)?? ) — write it explicitly:
        //   ω × q = (ωy qz - ωz qy, ωz qx - ωx qz, ωx qy - ωy qx)
        //   → dX/dω = (0,  qz, -qy),  dY/dω = (-qz, 0, qx),  dZ/dω = (qy, -qx, 0)
        const Ju0 = duX*0     + duZ*qy,   Ju1 = duX*qz   + duZ*(-qx), Ju2 = duX*(-qy) + duZ*0;
        const Jv0 = dvY*(-qz) + dvZ*qy,   Jv1 = dvY*0    + dvZ*(-qx), Jv2 = dvY*qx    + dvZ*0;
        const Ju3 = duX, Ju4 = 0,   Ju5 = duZ;
        const Jv3 = 0,   Jv4 = dvY, Jv5 = dvZ;
        const Ju = solveRot ? [Ju0, Ju1, Ju2, Ju3, Ju4, Ju5] : [0, 0, 0, Ju3, Ju4, Ju5];
        const Jv = solveRot ? [Jv0, Jv1, Jv2, Jv3, Jv4, Jv5] : [0, 0, 0, Jv3, Jv4, Jv5];
        for (let a = 0; a < 6; a++) {
          Jtr[a] += w * (Ju[a]*ru + Jv[a]*rv);
          for (let b = a; b < 6; b++) JtJ[a*6+b] += w * (Ju[a]*Ju[b] + Jv[a]*Jv[b]);
        }
      }
      if (wsum === 0) break;
      const rms = Math.sqrt(cost / wsum);
      this.rmsPx = rms;
      this.iterationsUsed = it + 1;
      if (rms < 1e-4) break;

      // symmetric fill + LM damping, then solve (JtJ + λ diag) x = -Jtr
      for (let a = 0; a < 6; a++) for (let b = 0; b < a; b++) JtJ[a*6+b] = JtJ[b*6+a];
      for (let a = 0; a < 6; a++) JtJ[a*6+a] *= (1 + lambda);
      if (!solveRot) for (let a = 0; a < 3; a++) JtJ[a*6+a] = 1;   // rotation rows: identity (x=0)
      if (!solve6(JtJ, Jtr, x)) break;

      // apply update: R ← exp([−x]×)·R ... we solved for step that reduces
      // residual, so apply δ = -x
      const wx = -x[0], wy = -x[1], wz = -x[2];
      const ang = Math.hypot(wx, wy, wz);
      if (solveRot && ang > 1e-12) {
        this._q.setFromAxisAngle(_axis.set(wx/ang, wy/ang, wz/ang), Math.min(ang, 0.5));
        this._dR.setFromMatrix4(_m4.makeRotationFromQuaternion(this._q));
        this.R.premultiply(this._dR);
      }
      t.x -= x[3]; t.y -= x[4]; t.z -= x[5];
      if (t.z > -5) t.z = -5;

      // adaptive damping
      if (cost < lastCost) lambda = Math.max(1e-6, lambda * 0.3); else lambda = Math.min(1, lambda * 10);
      lastCost = cost;
    }

    // Divergence guard: if the fit is terrible and we have a MediaPipe seed, re-init next frame.
    if (this.rmsPx > 0.15 && initMatrix) this._hasPose = false;

    // Re-orthonormalise R every frame (accumulated updates drift slightly).
    orthonormalize(this.R);

    const e = this._out.elements;
    e[0] = R[0]*s; e[1] = R[1]*s; e[2]  = R[2]*s;  e[3]  = 0;
    e[4] = R[3]*s; e[5] = R[4]*s; e[6]  = R[5]*s;  e[7]  = 0;
    e[8] = R[6]*s; e[9] = R[7]*s; e[10] = R[8]*s;  e[11] = 0;
    e[12] = t.x;   e[13] = t.y;   e[14] = t.z;     e[15] = 1;
    return this._out.elements;
  }
}

const _axis = new Vector3();
const _m4 = new Matrix4();

function orthonormalize(R) {
  const e = R.elements;
  const c0 = new Vector3(e[0], e[1], e[2]).normalize();
  const c1 = new Vector3(e[3], e[4], e[5]);
  c1.sub(c0.clone().multiplyScalar(c0.dot(c1))).normalize();
  const c2 = new Vector3().crossVectors(c0, c1);
  e[0]=c0.x; e[1]=c0.y; e[2]=c0.z; e[3]=c1.x; e[4]=c1.y; e[5]=c1.z; e[6]=c2.x; e[7]=c2.y; e[8]=c2.z;
}

// Gaussian elimination with partial pivoting for a 6×6 system A x = b.
function solve6(A, b, x) {
  const n = 6;
  const M = new Float64Array(n * (n + 1));
  for (let r = 0; r < n; r++) { for (let c = 0; c < n; c++) M[r*(n+1)+c] = A[r*n+c]; M[r*(n+1)+n] = b[r]; }
  for (let c = 0; c < n; c++) {
    let p = c, best = Math.abs(M[c*(n+1)+c]);
    for (let r = c + 1; r < n; r++) { const v = Math.abs(M[r*(n+1)+c]); if (v > best) { best = v; p = r; } }
    if (best < 1e-12) return false;
    if (p !== c) for (let k = 0; k <= n; k++) { const tmp = M[c*(n+1)+k]; M[c*(n+1)+k] = M[p*(n+1)+k]; M[p*(n+1)+k] = tmp; }
    for (let r = c + 1; r < n; r++) {
      const fct = M[r*(n+1)+c] / M[c*(n+1)+c];
      if (fct === 0) continue;
      for (let k = c; k <= n; k++) M[r*(n+1)+k] -= fct * M[c*(n+1)+k];
    }
  }
  for (let r = n - 1; r >= 0; r--) {
    let sum = M[r*(n+1)+n];
    for (let k = r + 1; k < n; k++) sum -= M[r*(n+1)+k] * x[k];
    x[r] = sum / M[r*(n+1)+r];
  }
  return true;
}
